<script setup lang="ts">
import {
  Image as DatocmsImage,
  StructuredText as DatocmsStructuredText,
} from 'vue-datocms'

import DatoSectionContainer from '~/components/dato/DatoSectionContainer.vue'

import { isNonNullAndDefined } from '~/utils/type-guards'

import type { TestimonialCarouselFragment } from '~/graphql/generated'

type Props = TestimonialCarouselFragment

const props = withDefaults(defineProps<Props>(), {
  testimonials: () => [],
})

// Create 4 copies to ensure smooth looping
const duplicatedTestimonials = computed(() => [
  ...props.testimonials,
  ...props.testimonials,
  ...props.testimonials,
  ...props.testimonials,
])
</script>

<template>
  <DatoSectionContainer takes-full-screen-width has-no-mobile-margins>
    <div
      class="testimonial-carousel tw-relative tw-w-full tw-overflow-x-hidden tw-overflow-y-hidden"
    >
      <!-- Carousel container -->
      <ul
        class="animate-scroll-left tw-m-0 tw-flex tw-w-max tw-list-none tw-flex-nowrap tw-gap-2xl tw-p-0"
      >
        <li
          v-for="(item, index) in duplicatedTestimonials"
          :key="`testimonial-${index}`"
          class="tw-flex tw-w-[200px] tw-shrink-0 tw-flex-col tw-gap-md tw-rounded-lg tw-bg-fill-discrete3 tw-p-md md:tw-w-[300px] lg:tw-w-[320px]"
        >
          <!-- Profile section -->
          <div class="tw-flex tw-items-center tw-gap-sm">
            <DatocmsImage
              v-if="isNonNullAndDefined(item.profilImage?.responsiveImage)"
              :data="item.profilImage.responsiveImage"
              class="tw-size-4xl tw-rounded-full tw-object-cover"
            />
            <div class="tw-flex tw-flex-col">
              <span class="tw-text-body-sm tw-font-medium md:tw-text-body">{{
                item.name
              }}</span>
              <span v-if="item.role" class="tw-text-body-sm tw-text-discrete">{{
                item.role
              }}</span>
            </div>
          </div>
          <!-- Quote section -->
          <div class="tw-flex-1">
            <DatocmsStructuredText
              v-if="item.quote"
              :data="item.quote"
              class="tw-text-body-xs tw-text-discrete md:tw-text-body-sm"
            />
          </div>
        </li>
      </ul>
    </div>
  </DatoSectionContainer>
</template>

<style scoped lang="scss">
@keyframes scrollLeft {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-25%);
  }
}

@media screen and (prefers-reduced-motion: no-preference) {
  .animate-scroll-left {
    animation: scrollLeft 60s linear infinite;
  }
}

/* Hide scrollbar but keep functionality */
@media (max-width: 768px) {
  div::-webkit-scrollbar {
    display: none;
  }
  div {
    -ms-overflow-style: none;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
}

:deep(mark) {
  @apply tw-bg-transparent tw-font-bold tw-text-primary;
}
</style>
